import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2 } from "lucide-react";
import { insuranceTypes } from "../mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  insuranceType: insuranceTypes[0],
  message: "",
};

const QuoteForm = ({ compact = false, defaultType }) => {
  const [form, setForm] = useState({
    ...initial,
    insuranceType: defaultType || initial.insuranceType,
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const update = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({
        title: "Missing information",
        description: "Please fill in your name, email and phone.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        insuranceType: form.insuranceType,
        message: form.message,
      });
      toast({
        title: "Quote request received!",
        description:
          "Thank you. A Cloverdale Insurance agent will contact you shortly.",
      });
      setForm({ ...initial, insuranceType: defaultType || initial.insuranceType });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description:
          "We couldn't submit your request. Please call us at 604-575-2222.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-[15px] text-[#0a2b52] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0066b3] focus:border-transparent transition-shadow";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <label className="block text-sm font-medium mb-1.5 text-[#0a2b52]">
            Full Name
          </label>
          <input
            name="name"
            value={form.name}
            onChange={update}
            placeholder="John Doe"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5 text-[#0a2b52]">
            Phone
          </label>
          <input
            name="phone"
            value={form.phone}
            onChange={update}
            placeholder="604-000-0000"
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5 text-[#0a2b52]">
          Email
        </label>
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={update}
          placeholder="you@example.com"
          className={inputCls}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5 text-[#0a2b52]">
          Insurance Type
        </label>
        <select
          name="insuranceType"
          value={form.insuranceType}
          onChange={update}
          className={inputCls}
        >
          {insuranceTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5 text-[#0a2b52]">
          Message
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={update}
          rows={compact ? 3 : 4}
          placeholder="Tell us what you'd like to insure..."
          className={inputCls + " resize-none"}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="brand-btn w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Sending...
          </>
        ) : (
          <>
            <Send size={18} /> Request Free Quote
          </>
        )}
      </button>
      <p className="text-xs text-slate-400 text-center">
        We'll never share your information. No obligation.
      </p>
    </form>
  );
};

export default QuoteForm;
